import { useTranslation } from "react-i18next";
import { useNavigate, useLocation } from 'react-router-dom';
import qs from 'qs';
import { Button } from '../../components/styles/Button.styled';
import {
    BarChart, Bar, Cell, XAxis, YAxis, CartesianGrid,
    Tooltip, ResponsiveContainer, Text
} from 'recharts';
import { Image } from "react-bootstrap";
import { NumericFormat } from 'react-number-format';
import { TduContactLaterSection } from '../styles/GetUsage.styled';
import { SHOW_MYACCOUNT_OPTION } from '../../common/data';

export const ShowUsage = ({
    source,
    data,
    unusualMonth,
    isPredictedData,
    showSmtView,
    showMyaccountView
}: {
    source: string
    data: any,
    unusualMonth: string[],
    isPredictedData: boolean,
    showSmtView: Function,
    showMyaccountView: Function
}) => {
    const { t } = useTranslation('usage');
    const navigate = useNavigate();
    const location = useLocation();
    const chartTheme = {
        axis: 'var(--color-muted)',
        axisUnusual: 'var(--brand-hover)',
        tooltipText: 'var(--ink-inverse)',
        tooltipBackground: 'rgba(0, 0, 0, .6)',
        hoverFill: 'rgba(var(--brand-rgb), 0.12)',
        barFill: 'var(--brand)',
    };
    
    const months = [t('Jan', {ns:'common'}), t('Feb', {ns:'common'}), t('Mar', {ns:'common'}), t('Apr', {ns:'common'}), t('May', {ns:'common'}), t('Jun', {ns:'common'}),
    t('Jul', {ns:'common'}), t('Aug', {ns:'common'}), t('Sep', {ns:'common'}), t('Oct', {ns:'common'}), t('Nov', {ns:'common'}), t('Dec', {ns:'common'})];

    let chartData = [];
    let totalUsage = 0;
    let usage: string[] = [];

    for (let i in data) {
        usage.push(data[i]);
    }

    for (let i in usage) {
        totalUsage += parseInt(usage[i]);
        chartData.push({ name: months[parseInt(i)], value: parseInt(usage[i]) })
    }

    const handleContinue = () => {
        const { address, esiid, zipcode, city, state, utcode } = qs.parse(location.search, { ignoreQueryPrefix: true });
        let params = `?address=${encodeURIComponent(`${address}`)}&esiid=${esiid}&zipcode=${zipcode}&city=${(encodeURIComponent(`${city}`))}&state=${state}&usagefrom=${source}&utcode=${utcode}`;
        if (!isPredictedData && usage) {
            params += `&actualusage=${usage.join('-')}`;
        }
        // parameter 'actualusage' can only be put in the end at the moment
        console.log(location.search,params)
        if (location.search === params) {
            navigate(0);
        }
        else {
            navigate(`/plans${params}`);
        }
    }

    return (
        <div>
            <div className="text-center">
                <Image src="/images/icon-check.png" alt="" width="64" />
                <h2 className="mt-3">{
                    isPredictedData ? t('Thank you for your patience') :
                    t('Your usage insight by month')
                }</h2>
                {
                    isPredictedData &&
                    <p>{t('But it seems like your address does not support instant data sharing with your utility company at this time.')} {t('Below is the forecast usage data based on zip code and your house size')}</p>
                }
            </div>
            <div className="d-flex align-items-center justify-content-center mt-5">
                <div style={{flex:1}} className="text-center"><div style={{fontSize:14}}>{t('Annual Usage')}:</div><b className="ms-1"><NumericFormat value={totalUsage} displayType={'text'} thousandSeparator={true} suffix=" kWh" /></b></div>
                <div style={{flex:1}} className="text-center"><div style={{fontSize:14}}>{t('Avg. Monthly Usage')}:</div><b className="ms-1"><NumericFormat value={Math.round(totalUsage / 12 * 100) / 100} displayType={'text'} thousandSeparator={true} suffix=" kWh" /></b></div>
            </div>
            <div className="mt-3" style={{ overflow: 'hidden' }}>
                <ResponsiveContainer width="100%" height={260}>
                    <BarChart
                        data={chartData}
                        margin={{
                            top: 12, right: 24, left: -12, bottom: 0,
                        }}
                    >
                        <XAxis
                            dataKey="name"
                            tick={(e) => {
                                const { payload: { value } } = e;
                                let unusual = false;

                                const monthNumber = `${months.indexOf(value) + 1}`;
                                if (unusualMonth.indexOf(monthNumber) >= 0) {
                                    e["fill"] = chartTheme.axisUnusual;
                                    unusual = true;
                                }
                                else {
                                    e["fill"] = chartTheme.axis;
                                    unusual = false;
                                }
                                return <Text {...e} style={{fontSize:11}}>{`${value}${unusual ? ' *' : ''}`}</Text>
                            }}
                            stroke={chartTheme.axis}
                            interval={0}
                        />
                        <YAxis tick={{fontSize:11, fill: chartTheme.axis}} stroke="var(--surface-light)" />
                        <CartesianGrid vertical={false} strokeDasharray="2" />
                        <Tooltip 
                            itemStyle={{
                                fontSize: 13,
                                fontWeight: 'bold',
                                color: chartTheme.tooltipText
                            }}
                            labelStyle={{
                                fontSize: 11,
                                fontWeight: 'normal',
                                color: chartTheme.tooltipText
                            }}
                            contentStyle={{
                                backgroundColor: chartTheme.tooltipBackground,
                                border: 0,
                                borderRadius: 4
                            }}
                            cursor={{
                                fill: chartTheme.hoverFill
                            }}
                            content={({ active, payload, label }) => {
                                if (active && label && payload) {
                                    return (
                                        <div className="chart-tooltip">
                                            <div className="date">{label}</div>
                                            <div className="value">{payload[0].value + 'kwh'}</div>
                                        </div>
                                    );
                                }
                                return null;
                            }}
                        />
                        <Bar dataKey="value"
                            isAnimationActive={true}
                            radius={10}
                            maxBarSize={10}
                        >
                            {chartData.map((entry, idx) => (
                                <Cell key={idx} fill={chartTheme.barFill} />
                            ))}
                        </Bar>
                    </BarChart>
                </ResponsiveContainer>
            </div>
            {
                unusualMonth.length > 0 &&
                <p style={{fontSize:13,color:'var(--color-muted)',lineHeight:1.1,marginTop:12}}>* {t('Changed REP during this month and the calculation of electricity usage differs from other months')}</p>
            }
            <div className="text-center mt-5">
                <Button onClick={handleContinue}>{
                    isPredictedData ? t('Continue with Forecast Usage') :
                    t('View Plans')
                }</Button>
            </div>
            {
                isPredictedData &&
                <TduContactLaterSection>
                    <p>{t("Or Click below to get your usage data by logging on to your existing energy provider's account or manually entering Smart Meter Texas (SMT) number")}</p>
                    <div id="get-usage-option-wrapper">
                        <div className="get-usage-option text-start" onClick={() => showSmtView()}>
                            <Image src="/images/icon-smart-meter.png" width="64" alt="" className="me-3" />
                            <div>
                                <h5>{t('Use Smart Meter Texas (SMT) Data')}</h5>
                                <p>{t('Instant access of your usage history through your smart meter number')}</p>
                            </div>
                        </div>
                        {SHOW_MYACCOUNT_OPTION && (
                            <div className="get-usage-option text-start" onClick={() => showMyaccountView()}>
                                <Image src="/images/icon-myaccount.png" width="64" alt="" className="me-3" />
                                <div>
                                    <h5>{t('MyAccount')}</h5>
                                    <p>{t('Instant access of your usage history through MyAccount')}</p>
                                </div>
                            </div>
                        )}
                    </div>
                </TduContactLaterSection>
            }
        </div>
    )
}